import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  Download,
  Home,
  EmojiEvents,
  Receipt,
  TrendingUp,
  ArrowForward,
  CreditCard,
} from '@mui/icons-material';
import { paymentAPI } from '../api/payment.api';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(41, 30, 108, 0.15)',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
}));

const SuccessCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(76, 175, 80, 0.05)',
  border: '2px solid #4caf50',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const InfoCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(41, 30, 108, 0.1)',
  },
}));

const StyledButton = styled(Button)({
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const UpgradeSuccess = () => {
  const navigate = useNavigate();
  const [upgradeData, setUpgradeData] = useState(null);

  useEffect(() => {
    const upgradeCompleted = localStorage.getItem('upgradeCompleted');
    if (!upgradeCompleted) {
      navigate('/dashboard');
      return;
    }

    const data = localStorage.getItem('upgradeData');
    if (data) {
      setUpgradeData(JSON.parse(data));
    }

    // Clean up after displaying
    return () => {
      localStorage.removeItem('upgradeCompleted');
      localStorage.removeItem('upgradeData');
    };
  }, []);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const formatAmount = (amount) => {
    if (typeof amount === 'number') {
      return (amount / 100).toFixed(2);
    }
    return '0.00';
  };

  if (!upgradeData) {
    return (
      <PageWrapper>
        <Container maxWidth="md">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <Typography variant="h6">Loading...</Typography>
          </Box>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        <StyledPaper>
          {/* Success Header */}
          <SuccessCard>
            <CardContent>
              <Box textAlign="center">
                <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                <Typography
                  variant="h3"
                  sx={{
                    fontFamily: "'Oswald', sans-serif",
                    fontWeight: 700,
                    color: '#4caf50',
                    mb: 1,
                  }}
                >
                  UPGRADE SUCCESSFUL!
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Your event registration has been successfully upgraded
                </Typography>
              </Box>
            </CardContent>
          </SuccessCard>

          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              {/* Upgrade Details */}
              <InfoCard sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <TrendingUp sx={{ mr: 1, color: '#291e6c' }} />
                    <Typography variant="h6" fontWeight="bold">
                      Upgrade Details
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Upgraded From
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {upgradeData.fromEvent}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="center">
                      <ArrowForward sx={{ color: '#4caf50' }} />
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Upgraded To
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        {upgradeData.toEvent}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Differential Amount
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ₹{upgradeData.differentialAmount}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </InfoCard>

              {/* Payment Details */}
              <InfoCard>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <CreditCard sx={{ mr: 1, color: '#291e6c' }} />
                    <Typography variant="h6" fontWeight="bold">
                      Payment Details
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Upgrade Invoice
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {upgradeData.upgradeInvoiceNumber}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Payment ID
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {upgradeData.paymentId}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Total Paid
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ₹{formatAmount(upgradeData.totalPaid)}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip label="Completed" color="success" size="small" sx={{ fontWeight: 600 }} />
                    </Box>
                  </Stack>
                </CardContent>
              </InfoCard>
            </Grid>

            {/* Right Column - Actions & Info */}
            <Grid item xs={12} md={6}>
              {/* Next Steps */}
              <InfoCard sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Next Steps
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={2}>
                    <StyledButton
                      variant="contained"
                      fullWidth
                      startIcon={<Home />}
                      onClick={handleGoToDashboard}
                      sx={{ backgroundColor: '#291e6c', '&:hover': { backgroundColor: '#1f1750' } }}
                    >
                      Go to Dashboard
                    </StyledButton>
                  </Stack>
                </CardContent>
              </InfoCard>

              {/* Important Information */}
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" mb={0.5}>
                  Confirmation Sent
                </Typography>
                <Typography variant="body2">
                  A confirmation email with your upgrade invoice has been sent to your registered email.
                </Typography>
              </Alert>

              <Alert severity="info" icon={<EmojiEvents />} sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" mb={0.5}>
                  New Admit Card Generated
                </Typography>
                <Typography variant="body2">
                  Your admit card has been updated with the new event details and is now available in your dashboard.
                </Typography>
              </Alert>

              <Alert severity="warning" icon={<Receipt />}>
                <Typography variant="body2" fontWeight="bold" mb={0.5}>
                  Invoice Records
                </Typography>
                <Typography variant="body2">
                  Both your original invoice and upgrade invoice are available in your dashboard for your records.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </StyledPaper>
      </Container>
    </PageWrapper>
  );
};

export default UpgradeSuccess;